import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, ClipboardCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Loader, Pill } from "@/components/Bits";
import { CardDeck } from "@/components/CardDeck";
import { ReadingPanel } from "@/components/ReadingPanel";
import { PracticePanel } from "@/components/PracticePanel";

const TABS = [
  ["study", "Study"],
  ["reading", "Full material"],
  ["practice", "Practice"],
];

export default function TopicPage() {
  const { topicId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "study";
  const [topic, setTopic] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(`/topics/${topicId}/cards`)
      .then(({ data }) => setTopic(data.topic))
      .catch((err) => {
        toast.error(errMsg(err));
        navigate("/library");
      });
  }, [topicId, navigate]);

  if (!topic) return <Shell><Loader label="Opening topic…" /></Shell>;

  return (
    <Shell>
      <div className="reveal" data-testid="topic-page">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-slate-500">
          <Link to="/library" className="hover:text-slate-900">
            Library
          </Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span>{topic.subject_name}</span>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to={`/chapter/${topic.chapter_id}`} data-testid="topic-goto-chapter" className="hover:text-slate-900">
            {topic.chapter_name}
          </Link>
        </nav>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">{topic.title}</h1>
            <p className="mt-2 text-sm text-slate-500 max-w-2xl">{topic.summary}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Pill tone="blue">{topic.subject_name}</Pill>
              <Pill tone="slate">{topic.chapter_name}</Pill>
            </div>
          </div>
          <button
            data-testid="topic-take-assessment"
            onClick={() => navigate(`/topic/${topicId}/test`)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            <ClipboardCheck className="w-4 h-4" /> Topic assessment
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`topic-tab-${key}`}
              onClick={() => setParams({ tab: key })}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "study" && <CardDeck topicId={topicId} />}
          {tab === "reading" && <ReadingPanel topicId={topicId} />}
          {tab === "practice" && <PracticePanel scope="topic" id={topicId} label="Problems drawn from this topic only." />}
        </div>
      </div>
    </Shell>
  );
}
